import React, { useEffect, useState } from 'react';
import styles from './Dashboard.module.scss';
import { Link } from 'react-router-dom';
import PageHeader from '../../Layouts/PageHeader/PageHeader';
import { Card, Col, Row, Badge, Alert, Spinner, Button } from 'react-bootstrap';
import { api } from '../../api/authService';
import { useAuth } from '../Authentication/AuthProvider';
import AnnouncementSlider from '../Announcements/AnnouncementSlider';

const Dashboard = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState({
        attendance: null,
        pendingTasks: 0,
        pendingDocuments: 0,
        leaveBalance: 0,
        upcomingTrips: 0
    });
    const [loading, setLoading] = useState(true);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [announcements, setAnnouncements] = useState([]);
    const [announcementLoading, setAnnouncementLoading] = useState(false);
    const [dismissedAlerts, setDismissedAlerts] = useState([]);
    
    const [location, setLocation] = useState(null);
    const [locationError, setLocationError] = useState(null);

    useEffect(() => {
        fetchDashboardData();
        fetchAnnouncements();
        getLocation();
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        const handleFocus = () => fetchDashboardData();
        window.addEventListener('focus', handleFocus);
        return () => { clearInterval(timer); window.removeEventListener('focus', handleFocus); };
    }, []);

    const fetchDashboardData = async () => {
        try {
            const [attendanceRes, tasksRes, leaveRes] = await Promise.all([
                api.get('/attendance/today'),
                api.get('/tasks/assigned?status=todo'),
                api.get('/leave/balance')
            ]);
            setStats({
                attendance: attendanceRes.data?.data || null,
                pendingTasks: tasksRes.data?.pagination?.total || 0,
                pendingDocuments: 0,
                leaveBalance: leaveRes.data?.data?.[0]?.sisa || 0,
                overtimeHours: 0
            });
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAnnouncements = async () => {
        setAnnouncementLoading(true);
        try {
            const res = await api.get('/announcements');
            setAnnouncements(res.data?.data || []);
        } catch (err) {
            console.error('Failed to fetch announcements:', err);
        } finally {
            setAnnouncementLoading(false);
        }
    };
    
    const getLocation = () => {
        setLocationError(null);
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const loc = {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                    };
                    setLocation(loc);
                },
                (error) => {
                    setLocationError('Izin GPS ditolak/tidak aktif');
                }
            );
        } else {
            setLocationError('Browser tidak mendukung lokasi');
        }
    };

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Selamat Pagi';
        if (hour < 15) return 'Selamat Siang';
        if (hour < 18) return 'Selamat Sore';
        return 'Selamat Malam';
    };

    const formatTime = (time) => {
        if (!time) return '--:--';
        if (time.includes('T')) return time.split('T')[1].substring(0, 5);
        return time.substring(0, 5);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const userProfile = user?.data || user;
    const myCompanyId = userProfile?.company_id;

    const urgentAnnouncements = announcements.filter(a => a.tipe === 'urgent');
    const regularAnnouncements = announcements.filter(a => a.tipe === 'reguler');

    const quickActions = [
        { to: '/cuti/request', icon: 'fe-calendar', label: 'Ajukan Cuti', bg: 'bg-success-transparent', text: 'text-success' },
        { to: '/nadine/create', icon: 'fe-file-text', label: 'Buat Surat', bg: 'bg-warning-transparent', text: 'text-warning' },
        { to: '/mytask', icon: 'fe-check-square', label: 'Lihat Tugas', bg: 'bg-primary-transparent', text: 'text-primary' },
        { to: '/lembur', icon: 'fe-clock', label: 'Lembur', bg: 'bg-danger-transparent', text: 'text-danger' }
    ];

    return (
        <div className="mt-4">
            <PageHeader titles={`${getGreeting()}, ${userProfile?.nama_lengkap?.split(' ')[0] || userProfile?.name?.split(' ')[0] || ''}! 👋`} active="HR Dashboard" items={['Home']} />

            {/* Live Clock & Date */}
            <div className="d-flex justify-content-between align-items-end mb-4">
               <div>
                  <h4 className="mb-1 text-muted fw-bold" style={{ fontSize: '1.2rem' }}>
                      {currentTime.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </h4>
               </div>
               <div className="d-flex align-items-center">
                   <Card className="border-0 shadow-sm m-0 bg-white shadow-sm" style={{ borderRadius: 10 }}>
                       <Card.Body className="py-2 px-3 d-flex align-items-center gap-2">
                           <i className="fe fe-clock fs-5 text-primary"></i>
                           <span className="fw-bold fs-4 text-monospace text-dark">
                               {currentTime.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                           </span>
                       </Card.Body>
                   </Card>
               </div>
            </div>

            {/* Priority Alerts */}
            {urgentAnnouncements.filter(a => !dismissedAlerts.includes(a.id)).length > 0 && (
                <div className="mb-4">
                    {urgentAnnouncements.filter(a => !dismissedAlerts.includes(a.id)).map(a => (
                        <Alert 
                            key={a.id} 
                            variant="warning" 
                            className="alert-dismissible fade show mb-2 d-flex align-items-center border-warning shadow-sm" 
                            role="alert" 
                            style={{ background: '#FFFBEB', borderRadius: '10px' }}
                            onClose={() => setDismissedAlerts(prev => [...prev, a.id])}
                            dismissible
                        >
                            <span className="alert-inner--icon me-3"><i className="fe fe-alert-triangle fs-3 pt-1 text-warning"></i></span>
                            <span className="alert-inner--text flex-grow-1">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <strong className="fs-15 text-dark">{a.judul}</strong>
                                    {a.company?.nama && <Badge bg="warning-transparent" className="text-warning-emphasis px-2 py-1 border border-warning-transparent">{a.company.nama}</Badge>}
                                </div>
                                <span className="d-block text-muted mt-1 fs-13">{a.konten?.length > 150 ? a.konten.substring(0, 150) + '...' : a.konten}</span>
                            </span>
                        </Alert>
                    ))}
                </div>
            )}

            {/* ROW 1: 4-Column Stat Cards */}
            <Row>
                <Col lg={12} md={12} sm={12} xl={12}>
                    <Row>
                        <Col lg={6} md={6} sm={12} xxl={3}>
                            <Card className="overflow-hidden bg-white shadow-sm border-0" style={{ borderRadius: '12px' }}>
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center">
                                        <div className="mt-1">
                                            <h6 className="text-muted fw-semibold mb-2">Tugas Pending</h6>
                                            <h2 className="mb-0 fw-bold text-dark">{stats.pendingTasks}</h2>
                                        </div>
                                        <div className="ms-auto">
                                            <div className="d-flex align-items-center justify-content-center bg-primary-transparent rounded-circle mt-1" style={{ width: 56, height: 56 }}>
                                                <i className="fe fe-check-square fs-2 text-primary"></i>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-3 text-muted fs-12">
                                        <span className="text-primary me-1"><i className="fe fe-info"></i></span>
                                        Tugas aktif saat ini
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        
                        <Col lg={6} md={6} sm={12} xxl={3}>
                            <Card className="overflow-hidden bg-white shadow-sm border-0" style={{ borderRadius: '12px' }}>
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center">
                                        <div className="mt-1">
                                            <h6 className="text-muted fw-semibold mb-2">Surat Menunggu</h6>
                                            <h2 className="mb-0 fw-bold text-dark">{stats.pendingDocuments}</h2>
                                        </div>
                                        <div className="ms-auto">
                                            <div className="d-flex align-items-center justify-content-center bg-warning-transparent rounded-circle mt-1" style={{ width: 56, height: 56 }}>
                                                <i className="fe fe-file-text fs-2 text-warning"></i>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-3 text-muted fs-12">
                                        <span className="text-warning me-1"><i className="fe fe-info"></i></span>
                                        Belum diproses
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        
                        <Col lg={6} md={6} sm={12} xxl={3}>
                            <Card className="overflow-hidden bg-white shadow-sm border-0" style={{ borderRadius: '12px' }}>
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center">
                                        <div className="mt-1">
                                            <h6 className="text-muted fw-semibold mb-2">Sisa Cuti</h6>
                                            <h2 className="mb-0 fw-bold text-dark">{stats.leaveBalance}</h2>
                                        </div>
                                        <div className="ms-auto">
                                            <div className="d-flex align-items-center justify-content-center bg-success-transparent rounded-circle mt-1" style={{ width: 56, height: 56 }}>
                                                <i className="fe fe-calendar fs-2 text-success"></i>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-3 text-muted fs-12">
                                        <span className="text-success me-1"><i className="fe fe-info"></i></span>
                                        Hari cuti tahunan
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        
                        <Col lg={6} md={6} sm={12} xxl={3}>
                            <Card className="overflow-hidden bg-white shadow-sm border-0" style={{ borderRadius: '12px' }}>
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center">
                                        <div className="mt-1">
                                            <h6 className="text-muted fw-semibold mb-2">Lembur</h6>
                                            <h2 className="mb-0 fw-bold text-dark">{stats.overtimeHours}</h2>
                                        </div>
                                        <div className="ms-auto">
                                            <div className="d-flex align-items-center justify-content-center bg-danger-transparent rounded-circle mt-1" style={{ width: 56, height: 56 }}>
                                                <i className="fe fe-clock fs-2 text-danger"></i>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-3 text-muted fs-12">
                                        <span className="text-danger me-1"><i className="fe fe-info"></i></span>
                                        Menunggu persetujuan
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Col>
            </Row>

            {/* ROW 2: Mixed Content */}
            <Row>
                <Col md={12} sm={12} lg={12} xl={8} xxl={8}>
                    {/* Aksi Cepat Card Wrapper */}
                    <Card className="bg-white shadow-sm border-0 mb-4" style={{ borderRadius: '12px' }}>
                        <Card.Body className="p-4">
                            <h5 className="fw-bold mb-4 text-dark mb-4">Aksi Cepat</h5>
                            <Row className="g-2">
                                {quickActions.map((a, i) => (
                                    <Col xs={6} sm={3} key={i}>
                                        <Link to={`${process.env.PUBLIC_URL}${a.to}`} className="text-decoration-none text-dark d-flex h-100">
                                            <div className="p-3 text-center d-flex flex-column align-items-center justify-content-center w-100 rounded-3 hover-shadow border bg-white border-light shadow-sm">
                                                <div className={`d-inline-flex align-items-center justify-content-center rounded-circle ${a.bg} ${a.text} mb-2`} style={{ width: 52, height: 52, flexShrink: 0 }}>
                                                    <i className={`fe ${a.icon} fs-20`}></i>
                                                </div>
                                                <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: '12px', letterSpacing: '-0.2px' }}>{a.label}</h6>
                                            </div>
                                        </Link>
                                    </Col>
                                ))}
                            </Row>
                        </Card.Body>
                    </Card>
                    
                    {/* Hover effects specific to quick actions */}
                    <style>{`
                        .hover-shadow { transition: all 0.2s ease-in-out; }
                        .hover-shadow:hover { box-shadow: 0 10px 25px rgba(0,0,0,0.05) !important; transform: translateY(-4px); border-color: #e2e8f0 !important; }
                    `}</style>

                    {/* Announcement Sliders */}
                    {announcementLoading ? (
                        <div className="text-center p-5 mt-4"><Spinner animation="border" variant="primary" /></div>
                    ) : announcements.length === 0 ? (
                        <Card className="shadow-sm border-0 mt-4 bg-white" style={{ borderRadius: '12px' }}>
                            <Card.Body className="text-center py-5 text-muted">
                                <i className="fe fe-folder fs-1 mb-3 text-light"></i>
                                <h5>Tidak ada pengumuman</h5>
                            </Card.Body>
                        </Card>
                    ) : (
                        <div className="mt-4">
                            {urgentAnnouncements.length > 0 && (
                                <AnnouncementSlider 
                                    title="🚨 Urgent & Penting" 
                                    announcements={urgentAnnouncements} 
                                    accentColor="#DC2626" 
                                    icon="urgent" 
                                />
                            )}
                            
                            {regularAnnouncements.length > 0 && (
                                <AnnouncementSlider 
                                    title="📢 Informasi & Pengumuman" 
                                    announcements={regularAnnouncements} 
                                    accentColor="#2563EB" 
                                    icon="general" 
                                />
                            )}
                        </div>
                    )}
                </Col>

                <Col md={12} sm={12} lg={12} xl={4} xxl={4}>
                    <Card className="bg-white shadow-sm border-0 h-100" style={{ borderRadius: '12px' }}>
                        <Card.Body className="p-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="fw-bold text-dark mb-0">Status Presensi Hari Ini</h5>
                                <Badge bg={stats.attendance?.has_clock_in ? 'success-transparent' : 'danger-transparent'} className={stats.attendance?.has_clock_in ? 'text-success' : 'text-danger'}>
                                    {stats.attendance?.has_clock_in ? 'Hadir' : 'Belum Absen'}
                                </Badge>
                            </div>
                            
                            <div className="rounded-3 overflow-hidden border border-light shadow-sm mb-3 bg-light d-flex align-items-center justify-content-center" style={{ height: 160, position: 'relative' }}>
                                {location ? (
                                    <iframe
                                        title="Current Location"
                                        width="100%"
                                        height="100%"
                                        frameBorder="0"
                                        style={{ border: 0 }}
                                        src={`https://www.google.com/maps?q=${location.latitude},${location.longitude}&hl=id&z=15&output=embed`}
                                        allowFullScreen
                                    ></iframe>
                                ) : (
                                    <div className="text-center py-4">
                                        <div className="mx-auto bg-primary-transparent rounded-circle d-flex align-items-center justify-content-center mb-2" style={{ width: 60, height: 60 }}>
                                            <i className="fe fe-map-pin text-primary" style={{ fontSize: 28 }}></i>
                                        </div>
                                        <p className="text-muted fs-12 mb-0">{locationError || 'Mengambil lokasi...'}</p>
                                        {!location && !locationError && <Spinner size="sm" variant="primary" className="mt-2" />}
                                        {locationError && <Button variant="link" size="sm" onClick={getLocation} className="p-0 text-primary">Coba Lagi</Button>}
                                    </div>
                                )}
                            </div>
                            
                            <Row className="g-2 mb-3">
                                <Col xs={6}>
                                    <div className="p-3 bg-light rounded-3 h-100 border border-light-2">
                                        <div className="d-flex align-items-center mb-1">
                                            <div className="bg-success-transparent text-success rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: 24, height: 24 }}>
                                                <i className="fe fe-log-in fs-12"></i>
                                            </div>
                                            <span className="text-muted fs-11 fw-semibold">Masuk</span>
                                        </div>
                                        <h5 className="fw-bold mb-0 text-dark">{formatTime(stats.attendance?.waktu_masuk)}</h5>
                                        <small className="text-success fs-10 fw-bold">Hadir</small>
                                    </div>
                                </Col>
                                <Col xs={6}>
                                    <div className="p-3 bg-light rounded-3 h-100 border border-light-2">
                                        <div className="d-flex align-items-center mb-1">
                                            <div className="bg-danger-transparent text-danger rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: 24, height: 24 }}>
                                                <i className="fe fe-log-out fs-12"></i>
                                            </div>
                                            <span className="text-muted fs-11 fw-semibold">Pulang</span>
                                        </div>
                                        <h5 className="fw-bold mb-0 text-dark">{formatTime(stats.attendance?.waktu_keluar)}</h5>
                                        <small className="text-muted fs-10">Belum absen</small>
                                    </div>
                                </Col>
                            </Row>

                            <div className="mt-2">
                                <Link to={`${process.env.PUBLIC_URL}/presensi`} className="btn btn-primary d-flex align-items-center justify-content-center gap-2 w-100 py-2 fs-14 fw-bold shadow-sm" style={{ borderRadius: 8 }}>
                                   {stats.attendance?.has_clock_in ? (stats.attendance?.has_clock_out ? 'Lihat Detail' : 'Lakukan Clock Out') : 'Lakukan Clock In'}
                                   <i className="fe fe-arrow-right fs-13"></i>
                                </Link>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>


        </div>
    );
};

export default Dashboard;
